import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../../../lib/api.js';

// Load BenchItem rows for a given isoDate grouped by tier, with optimistic
// promote/archive + a 5-second Undo window. Promote-to-today returns the
// updated Edition; the caller (Editor.jsx) hands that back to useEditionDraft
// so the article list reflects the new row immediately.

const TIERS = [1, 2, 3, 4, 5, 6];
const UNDO_MS = 5000;

function emptyByTier() {
  return Object.fromEntries(TIERS.map((t) => [t, []]));
}

export function useBench(isoDate, { onEditionUpdate } = {}) {
  const [byTier, setByTier] = useState(emptyByTier());
  const [counts, setCounts] = useState({ total: 0, unpromoted: 0, byTier: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [tierFilter, setTierFilter] = useState(null); // null = all
  const [query, setQuery] = useState('');
  const [pendingUndo, setPendingUndo] = useState(null); // { benchId, prev, destination, timer }
  const undoTimerRef = useRef(null);

  const load = useCallback(async () => {
    if (!isoDate) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (tierFilter) params.set('tier', String(tierFilter));
      if (includeArchived) params.set('include', 'archived');
      if (query.trim()) params.set('q', query.trim());
      const qs = params.toString();
      const path = `/editions/${isoDate}/bench${qs ? `?${qs}` : ''}`;
      const data = await api.get(path);
      setByTier({ ...emptyByTier(), ...(data.byTier || {}) });
      setCounts(data.counts || { total: 0, unpromoted: 0, byTier: {} });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isoDate, tierFilter, includeArchived, query]);

  useEffect(() => {
    load();
  }, [load]);

  function patchRow(benchId, patch) {
    setByTier((prev) => {
      const next = { ...prev };
      for (const t of TIERS) {
        next[t] = (prev[t] || []).map((r) =>
          String(r._id) === String(benchId) ? { ...r, ...patch } : r
        );
      }
      return next;
    });
  }

  function clearUndo() {
    if (undoTimerRef.current) {
      clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
    }
    setPendingUndo(null);
  }

  const promote = useCallback(
    async (benchId, destination, { article } = {}) => {
      // Snapshot previous state for Undo.
      let prev = null;
      for (const t of TIERS) {
        const found = (byTier[t] || []).find((r) => String(r._id) === String(benchId));
        if (found) {
          prev = found;
          break;
        }
      }
      // Optimistically mark promoted (so the row falls out of the unpromoted view).
      patchRow(benchId, {
        promoted: { destination, promotedAt: new Date().toISOString() },
      });

      try {
        const body = { destination };
        // `article` carries the editor-confirmed draft from the bench preview
        // modal — when present the server uses it in place of the bench item's
        // raw fields (today destination only).
        if (article && destination === 'today') body.article = article;
        const data = await api.post(
          `/editions/${isoDate}/bench/${benchId}/promote`,
          body
        );
        if (data.benchItem) patchRow(benchId, data.benchItem);
        if (data.edition) onEditionUpdate?.(data.edition);

        // Arm Undo window.
        clearUndo();
        const timer = setTimeout(() => setPendingUndo(null), UNDO_MS);
        undoTimerRef.current = timer;
        setPendingUndo({ benchId, prev, destination, until: Date.now() + UNDO_MS });
        // Refresh counts in the background.
        load();
      } catch (err) {
        // Revert.
        if (prev) patchRow(benchId, { promoted: prev.promoted });
        setError(err.message);
        throw err;
      }
    },
    [byTier, isoDate, onEditionUpdate, load]
  );

  const unpromote = useCallback(
    async (benchId) => {
      // Optimistic clear of promoted.
      patchRow(benchId, { promoted: undefined });
      try {
        const data = await api.del(`/editions/${isoDate}/bench/${benchId}/promote`);
        if (data.edition) onEditionUpdate?.(data.edition);
        if (data.benchItem) patchRow(benchId, data.benchItem);
        load();
      } catch (err) {
        setError(err.message);
        load();
      }
    },
    [isoDate, onEditionUpdate, load]
  );

  const undo = useCallback(async () => {
    if (!pendingUndo) return;
    const { benchId } = pendingUndo;
    clearUndo();
    await unpromote(benchId);
  }, [pendingUndo, unpromote]);

  return {
    byTier,
    counts,
    loading,
    error,
    reload: load,
    includeArchived,
    setIncludeArchived,
    tierFilter,
    setTierFilter,
    query,
    setQuery,
    promote,
    unpromote,
    pendingUndo,
    undo,
    dismissUndo: clearUndo,
  };
}
